import * as React from "react";
import { useNavigate } from "react-router";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { apiUrl } from "../utils/api";
import { alleFenster } from "./windowManager/browserFenster/fensterRegister";

/* ──────────────────────────────────────────────────────────────────────────
   SchadenForm — neu gestaltet
   - keine Fluent UI Components (Input/Dropdown/Field/Button/...) mehr
   - reines HTML + makeStyles
   - keine Required-Regeln / Validierungen (alles speicherbar)
   ────────────────────────────────────────────────────────────────────────── */

const BRAND = {
  ink: "#2d142b",
  purple: "#4d2051",
  gold: "#f7a823",
  border: "#E3E0E7",
  borderDashed: "#ECE7F0",
  muted: "#7c7480",
  bg: "#fafafa",
  cardBg: "#fbf9fc",
  white: "#ffffff",
};

const useStyles = makeStyles({
  page: {
    display: "flex",
    justifyContent: "center",
    backgroundColor: BRAND.bg,
    minHeight: "100vh",
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
    fontFamily: "'Barlow', 'Segoe UI', Tahoma, sans-serif",
    color: BRAND.ink,
    boxSizing: "border-box",
  },
  shell: {
    width: "min(1080px, 100%)",
    position: "relative",
  },
  header: {
    paddingTop: "16px",
    paddingBottom: "12px",
    paddingLeft: "24px",
    paddingRight: "24px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    flexShrink: 0,
    backgroundColor: BRAND.white,
    position: "sticky",
    top: 0,
    zIndex: 100,
    ...shorthands.borderRadius("12px", "12px", "0", "0"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  headerRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
  },
  title: {
    justifySelf: "center",
    margin: 0,
    fontSize: "1.8rem",
    fontWeight: 400,
    fontFamily: "'Playfair Display', Georgia, serif",
    color: BRAND.ink,
  },
  closeBtn: {
    justifySelf: "end",
    appearance: "none",
    cursor: "pointer",
    width: "36px",
    height: "36px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    color: BRAND.muted,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    ":hover": {
      color: BRAND.ink,
      backgroundColor: BRAND.bg,
    },
  },
  progressTrack: {
    marginTop: "10px",
    height: "8px",
    width: "100%",
    backgroundColor: BRAND.borderDashed,
    overflow: "hidden",
    position: "relative",
    ...shorthands.borderRadius("999px"),
  },
  progressFill: {
    height: "100%",
    backgroundColor: BRAND.gold,
    transitionProperty: "width",
    transitionDuration: "200ms",
  },
  progressLabel: {
    marginTop: "4px",
    fontSize: "0.72rem",
    color: BRAND.muted,
    fontWeight: 600,
    textAlign: "right",
  },
  body: {
    backgroundColor: BRAND.white,
    paddingTop: "24px",
    paddingBottom: "96px",
    paddingLeft: "24px",
    paddingRight: "24px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    borderTop: "none",
    ...shorthands.borderRadius("0", "0", "12px", "12px"),
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  intro: {
    marginTop: 0,
    marginBottom: 0,
    color: BRAND.muted,
    fontSize: "0.86rem",
    lineHeight: 1.45,
  },
  section: {
    marginTop: "22px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("12px"),
    paddingTop: "18px",
    paddingBottom: "18px",
    paddingLeft: "20px",
    paddingRight: "20px",
    backgroundColor: BRAND.cardBg,
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: "10px",
    fontSize: "1.35rem",
    color: BRAND.ink,
    fontFamily: "'Playfair Display', Georgia, serif",
    fontWeight: 400,
    paddingBottom: "8px",
    ...shorthands.borderBottom("2px", "solid", BRAND.gold),
  },
  hint: {
    color: BRAND.muted,
    fontSize: "0.82rem",
    marginBottom: "12px",
  },
  fields: {
    display: "grid",
    rowGap: "14px",
    columnGap: "14px",
    gridTemplateColumns: "1fr",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "1fr 1fr",
      alignItems: "start",
    },
  },
  fieldsThree: {
    display: "grid",
    rowGap: "14px",
    columnGap: "14px",
    gridTemplateColumns: "1fr",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "1fr 1fr",
    },
    "@media (min-width: 1024px)": {
      gridTemplateColumns: "1fr 1fr 1fr",
    },
  },
  fullRow: {
    gridColumn: "1 / -1",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  fieldLabel: {
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  fieldHint: {
    fontSize: "0.72rem",
    color: BRAND.muted,
    fontStyle: "italic",
  },
  input: {
    appearance: "none",
    width: "100%",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: "inherit",
    fontSize: "0.92rem",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    boxSizing: "border-box",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("8px"),
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      outline: "none",
      boxShadow: `0 0 0 2px rgba(77,32,81,0.15)`,
    },
  },
  textarea: {
    appearance: "none",
    width: "100%",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: "inherit",
    fontSize: "0.92rem",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "10px",
    paddingRight: "10px",
    boxSizing: "border-box",
    minHeight: "120px",
    resize: "vertical",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("8px"),
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      outline: "none",
      boxShadow: `0 0 0 2px rgba(77,32,81,0.15)`,
    },
  },
  select: {
    appearance: "none",
    width: "100%",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: "inherit",
    fontSize: "0.92rem",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "32px",
    boxSizing: "border-box",
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='%237c7480' d='M6 8L0 0h12z'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("8px"),
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      outline: "none",
      boxShadow: `0 0 0 2px rgba(77,32,81,0.15)`,
    },
  },

  /* Search results dropdown */
  searchWrap: {
    position: "relative",
  },
  resultList: {
    marginTop: "4px",
    backgroundColor: BRAND.white,
    maxHeight: "260px",
    overflowY: "auto",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("8px"),
    boxShadow: "0 8px 16px rgba(45,20,43,0.10)",
  },
  resultButton: {
    appearance: "none",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    backgroundColor: "transparent",
    color: BRAND.ink,
    fontFamily: "inherit",
    fontSize: "0.88rem",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    textAlign: "left",
    ...shorthands.border("0"),
    ":hover": {
      backgroundColor: BRAND.bg,
    },
  },
  resultButtonSelected: {
    backgroundColor: "rgba(77,32,81,0.08)",
    color: BRAND.purple,
    fontWeight: 700,
  },
  resultMeta: {
    fontSize: "0.74rem",
    color: BRAND.muted,
    marginTop: "2px",
  },

  /* Selected filter chip */
  filterChip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    marginTop: "8px",
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "10px",
    paddingRight: "6px",
    backgroundColor: "rgba(77,32,81,0.08)",
    color: BRAND.purple,
    fontSize: "0.82rem",
    fontWeight: 600,
    ...shorthands.borderRadius("999px"),
  },
  filterChipBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontSize: "0.72rem",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "8px",
    paddingRight: "8px",
    ...shorthands.border("0"),
    ...shorthands.borderRadius("999px"),
    ":hover": {
      backgroundColor: BRAND.ink,
    },
  },

  /* Assignment cards */
  assignmentGrid: {
    display: "grid",
    rowGap: "12px",
    columnGap: "12px",
    gridTemplateColumns: "1fr",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "1fr 1fr 1fr",
    },
  },
  assignmentCard: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "12px",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderLeft("4px", "solid", BRAND.gold),
    ...shorthands.borderRadius("10px"),
  },
  assignmentIcon: {
    width: "36px",
    height: "36px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderRadius("8px"),
  },
  assignmentText: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    minWidth: 0,
  },
  assignmentLabel: {
    fontSize: "0.7rem",
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  assignmentValue: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: BRAND.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  assignmentMeta: {
    fontSize: "0.72rem",
    color: BRAND.muted,
  },

  /* Blocked overlay when no risk selected */
  blocked: {
    opacity: 0.5,
    pointerEvents: "none",
  },

  /* Footer */
  footer: {
    position: "sticky",
    bottom: 0,
    zIndex: 110,
    backgroundColor: BRAND.white,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "24px",
    paddingRight: "24px",
    display: "flex",
    justifyContent: "flex-end",
    columnGap: "10px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    borderTop: "none",
    ...shorthands.borderRadius("0", "0", "12px", "12px"),
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    marginTop: "-12px",
  },
  primaryBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontFamily: "inherit",
    fontSize: "0.92rem",
    fontWeight: 700,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    ":hover": {
      backgroundColor: BRAND.ink,
      ...shorthands.borderColor(BRAND.ink),
    },
    ":disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
  secondaryBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    color: BRAND.ink,
    fontFamily: "inherit",
    fontSize: "0.92rem",
    fontWeight: 600,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    ":hover": {
      backgroundColor: BRAND.bg,
    },
  },

  /* Toast */
  toast: {
    position: "fixed",
    top: "20px",
    right: "20px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "16px",
    paddingRight: "16px",
    zIndex: 200,
    boxShadow: "0 8px 18px rgba(45,20,43,0.18)",
    color: BRAND.white,
    fontSize: "0.88rem",
    fontWeight: 600,
    ...shorthands.borderRadius("8px"),
  },
  toastSuccess: { backgroundColor: "#15803d" },
  toastError: { backgroundColor: "#b91c1c" },
});

/* ──────────────────────────────────────────────────────────────────────────
   Types
   ────────────────────────────────────────────────────────────────────────── */

type RiskItem = {
  id: number;
  riskName: string;
  branch: string;
  contactID: number;
  contractID: number;
};

type ContactOption = {
  id: number;
  name: string;
};

type ContractOption = {
  id: number;
  number: string;
  contactID: number | null;
};

export interface SchadenFormValues {
  schaden?: string;
  kontakt?: string;
  vertrag?: string;
  schadentag?: string;
  schadenort?: string;
  beschreibung?: string;
  schadenhoehe?: string;
  sb?: string;
  zahlung?: string;
  erstellt?: string;
  anspruchsteller?: string;
  vorgangsNr?: string;
  sachbearbeiter?: string;
  status?: string;
  fahrer?: string;
  schadenursache?: string;
  meldung?: string;
  versicherteGefahr?: string;
}

interface SchadenFormProps {
  initialValues?: SchadenFormValues;
}

const STATUS_OPTIONS = ["Offen", "In Bearbeitung", "Abgeschlossen"];
const SACHBEARBEITER_OPTIONS = ["Max Müller", "Erika Mustermann"];
const VORGANG_OPTIONS = ["P-001", "P-002", "P-003"];

/* ──────────────────────────────────────────────────────────────────────────
   Component
   ────────────────────────────────────────────────────────────────────────── */

export const SchadenForm: React.FC<SchadenFormProps> = ({
  initialValues = {},
}) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const shellRef = React.useRef<HTMLDivElement | null>(null);
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const today = new Date().toISOString().split("T")[0];

  /* ── Form state — no required validation ── */
  const [schaden, setSchaden] = React.useState(initialValues.schaden ?? "");
  const [kontakt, setKontakt] = React.useState(initialValues.kontakt ?? "");
  const [vertrag, setVertrag] = React.useState(initialValues.vertrag ?? "");
  const [schadentag, setSchadentag] = React.useState(
    initialValues.schadentag ?? today,
  );
  const [schadenort, setSchadenort] = React.useState(
    initialValues.schadenort ?? "",
  );
  const [beschreibung, setBeschreibung] = React.useState(
    initialValues.beschreibung ?? "",
  );
  const [schadenhoehe, setSchadenhoehe] = React.useState(
    initialValues.schadenhoehe ?? "",
  );
  const [sb, setSb] = React.useState(initialValues.sb ?? "");
  const [zahlung, setZahlung] = React.useState(initialValues.zahlung ?? "");
  const [erstellt, setErstellt] = React.useState(
    initialValues.erstellt ?? today,
  );
  const [anspruchsteller, setAnspruchsteller] = React.useState(
    initialValues.anspruchsteller ?? "",
  );
  const [vorgangsNr, setVorgangsNr] = React.useState(
    initialValues.vorgangsNr ?? "",
  );
  const [sachbearbeiter, setSachbearbeiter] = React.useState(
    initialValues.sachbearbeiter ?? "",
  );
  const [status, setStatus] = React.useState(initialValues.status ?? "");
  const [fahrer, setFahrer] = React.useState(initialValues.fahrer ?? "");
  const [schadenursache, setSchadenursache] = React.useState(
    initialValues.schadenursache ?? "",
  );
  const [meldung, setMeldung] = React.useState(initialValues.meldung ?? "");
  const [versicherteGefahr, setVersicherteGefahr] = React.useState(
    initialValues.versicherteGefahr ?? "",
  );

  /* ── Filter/lookup state ── */
  const [allRisks, setAllRisks] = React.useState<RiskItem[]>([]);
  const [contactOptions, setContactOptions] = React.useState<ContactOption[]>(
    [],
  );
  const [contractOptions, setContractOptions] = React.useState<
    ContractOption[]
  >([]);
  const [contactSearch, setContactSearch] = React.useState("");
  const [contactResults, setContactResults] = React.useState<ContactOption[]>(
    [],
  );
  const [contractSearch, setContractSearch] = React.useState("");
  const [contractResults, setContractResults] = React.useState<
    ContractOption[]
  >([]);
  const [riskSearch, setRiskSearch] = React.useState("");
  const [selectedRiskBranch, setSelectedRiskBranch] = React.useState("");
  const [selectedRiskId, setSelectedRiskId] = React.useState<number | null>(
    null,
  );
  const [filterContactId, setFilterContactId] = React.useState<number | null>(
    null,
  );
  const [filterContractId, setFilterContractId] = React.useState<number | null>(
    null,
  );
  const [toast, setToast] = React.useState<{
    kind: "success" | "error";
    message: string;
  } | null>(null);

  const showToast = (kind: "success" | "error", message: string) => {
    setToast({ kind, message });
    window.setTimeout(() => setToast(null), 2400);
  };

  /* ── Load risks + minimal contact/contract pools for labels ── */
  React.useEffect(() => {
    const load = async () => {
      try {
        const [risksRes, contactsRes, contractsRes] = await Promise.all([
          fetch(apiUrl("risk-objects", { limit: "200", offset: "0" })),
          fetch(apiUrl("contact", { limit: "200", offset: "0" })),
          fetch(apiUrl("contracts", { limit: "200", offset: "0" })),
        ]);

        if (risksRes.ok) {
          const data = await risksRes.json();
          const items: RiskItem[] = (Array.isArray(data) ? data : []).map(
            (r: any) => ({
              id: Number(r.id ?? 0),
              riskName: String(r.name ?? r.RisikoOrt ?? r.riskLocation ?? "")
                .trim() ||
                `Risiko ${r.id}`,
              branch: String(r.riskType ?? r.Zweig ?? "").trim(),
              contactID: Number(r.contactID ?? r.contact?.id ?? 0),
              contractID: Number(r.contractID ?? r.contract?.id ?? 0),
            }),
          );
          setAllRisks(items.filter((r) => r.id > 0));
        }

        if (contactsRes.ok) {
          const data = await contactsRes.json();
          const items: ContactOption[] = (Array.isArray(data) ? data : []).map(
            (c: any) => ({
              id: Number(c.id ?? 0),
              name: String(
                c.displayName ??
                  [c.lastname, c.vorname].filter(Boolean).join(", ") ??
                  `Kontakt ${c.id}`,
              ),
            }),
          );
          setContactOptions(items.filter((c) => c.id > 0));
        }

        if (contractsRes.ok) {
          const data = await contractsRes.json();
          const items: ContractOption[] = (Array.isArray(data) ? data : []).map(
            (c: any) => ({
              id: Number(c.id ?? 0),
              number: String(c.contractNbr ?? `Vertrag ${c.id}`),
              contactID: c.contactID
                ? Number(c.contactID)
                : c.contact?.id
                ? Number(c.contact.id)
                : null,
            }),
          );
          setContractOptions(items.filter((c) => c.id > 0));
        }
      } catch (err) {
        console.error("Failed to load form data", err);
      }
    };
    void load();
  }, []);

  /* ── Contact search ── */
  React.useEffect(() => {
    const q = contactSearch.trim().toLowerCase();
    if (!q) {
      setContactResults([]);
      return;
    }
    const t = window.setTimeout(() => {
      const matches = contactOptions
        .filter((c) => c.name.toLowerCase().includes(q))
        .slice(0, 30);
      setContactResults(matches);
    }, 150);
    return () => window.clearTimeout(t);
  }, [contactSearch, contactOptions]);

  /* ── Contract search ── */
  React.useEffect(() => {
    const q = contractSearch.trim().toLowerCase();
    if (!q) {
      setContractResults([]);
      return;
    }
    const t = window.setTimeout(() => {
      const matches = contractOptions
        .filter((c) =>
          c.number.toLowerCase().includes(q) ||
          (filterContactId == null || c.contactID === filterContactId),
        )
        .filter((c) => c.number.toLowerCase().includes(q))
        .slice(0, 30);
      setContractResults(matches);
    }, 150);
    return () => window.clearTimeout(t);
  }, [contractSearch, contractOptions, filterContactId]);

  /* ── Filtered risks ── */
  const filteredRisks = React.useMemo(() => {
    const q = riskSearch.trim().toLowerCase();
    return allRisks.filter((r) => {
      if (selectedRiskBranch && r.branch !== selectedRiskBranch) return false;
      if (filterContactId != null && r.contactID !== filterContactId) return false;
      if (filterContractId != null && r.contractID !== filterContractId) return false;
      if (!q) return true;
      return r.riskName.toLowerCase().includes(q);
    });
  }, [allRisks, riskSearch, selectedRiskBranch, filterContactId, filterContractId]);

  const branchOptions = React.useMemo(
    () =>
      Array.from(
        new Set(allRisks.map((r) => r.branch).filter(Boolean)),
      ).sort(),
    [allRisks],
  );

  const selectedRisk = React.useMemo(
    () => allRisks.find((r) => r.id === selectedRiskId) ?? null,
    [allRisks, selectedRiskId],
  );

  const selectedContactName = React.useMemo(() => {
    if (!selectedRisk) return "";
    return (
      contactOptions.find((c) => c.id === selectedRisk.contactID)?.name ?? ""
    );
  }, [selectedRisk, contactOptions]);

  const selectedContractNbr = React.useMemo(() => {
    if (!selectedRisk) return "";
    return (
      contractOptions.find((c) => c.id === selectedRisk.contractID)?.number ?? ""
    );
  }, [selectedRisk, contractOptions]);

  const isKfzBranch =
    !!selectedRisk?.branch &&
    selectedRisk.branch.toLowerCase().includes("kfz");

  /* ── Progress: based on scroll ── */
  React.useEffect(() => {
    const onScroll = () => {
      const el = shellRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.scrollHeight - window.innerHeight;
      const seen = -rect.top;
      const pct = total > 0 ? Math.max(0, Math.min(1, seen / total)) : 0;
      setScrollProgress(pct);
    };
    alleFenster.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => alleFenster.removeEventListener("scroll", onScroll);
  }, []);

  /* ── Save (no validation rules) ── */
  const handleSave = async () => {
    const payload = {
      title: schaden,
      status,
      damageDate: schadentag,
      created: erstellt,
      claimAmount: schadenhoehe ? Number(schadenhoehe) : null,
      sb: sb ? Number(sb) : null,
      payment: zahlung,
      description: beschreibung,
      claimant: anspruchsteller,
      caseNbr: vorgangsNr,
      handler: sachbearbeiter,
      driver: fahrer,
      damageCause: schadenursache,
      reportType: meldung,
      insuredHazard: versicherteGefahr,
      damageLocation: schadenort,
      contactID: selectedRisk?.contactID ?? (kontakt ? null : null),
      contractID: selectedRisk?.contractID ?? null,
      riskID: selectedRisk?.id ?? null,
    };

    try {
      const res = await fetch(apiUrl("damage"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      showToast("success", "Schaden gespeichert");
      window.setTimeout(() => navigate("/damages"), 800);
    } catch (err) {
      console.error("Failed to save damage", err);
      showToast("error", "Speichern fehlgeschlagen");
    }
  };

  const headerTitle = selectedRisk?.branch
    ? `${selectedRisk.branch} Schaden anlegen`
    : "Schaden anlegen";

  /* ── Render ── */
  return (
    <div className={styles.page}>
      <div className={styles.shell} ref={shellRef}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerRow}>
            <div />
            <h1 className={styles.title}>{headerTitle}</h1>
            <button
              type="button"
              className={styles.closeBtn}
              aria-label="Schließen"
              onClick={() => navigate(-1)}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
          </div>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${Math.round(scrollProgress * 100)}%` }}
            />
          </div>
          <div className={styles.progressLabel}>
            {Math.round(scrollProgress * 100)}%
          </div>
        </div>

        {/* Body */}
        <div className={styles.body}>
          <p className={styles.intro}>
            Bitte erfassen Sie den Schaden in einem Durchgang. Alle Abschnitte
            sind auf einer Seite — kein Feld ist Pflicht, Sie können jederzeit
            speichern.
          </p>

          {/* Risiko auswählen */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Risiko auswählen</h3>
            <div className={styles.hint}>
              Optional: filtern Sie nach Kontakt oder Vertrag. Risiko ist nicht
              pflicht — Sie können den Schaden auch ohne Risiko speichern.
            </div>

            <div className={styles.fields}>
              {/* Kontaktfilter */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Kontaktfilter</label>
                <div className={styles.searchWrap}>
                  <input
                    className={styles.input}
                    type="text"
                    placeholder="Kontakt suchen"
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                  />
                  {contactResults.length > 0 && (
                    <div className={styles.resultList}>
                      {contactResults.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className={styles.resultButton}
                          onClick={() => {
                            setFilterContactId(c.id);
                            setContactSearch("");
                            setContactResults([]);
                          }}
                        >
                          <span>{c.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {filterContactId != null && (
                  <div className={styles.filterChip}>
                    <span>
                      Kontakt:{" "}
                      {contactOptions.find((c) => c.id === filterContactId)
                        ?.name ?? `Kontakt ${filterContactId}`}
                    </span>
                    <button
                      type="button"
                      className={styles.filterChipBtn}
                      onClick={() => {
                        setFilterContactId(null);
                        setFilterContractId(null);
                      }}
                    >
                      entfernen
                    </button>
                  </div>
                )}
              </div>

              {/* Vertragsfilter */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Vertragsfilter</label>
                <div className={styles.searchWrap}>
                  <input
                    className={styles.input}
                    type="text"
                    placeholder="Vertrag suchen"
                    value={contractSearch}
                    onChange={(e) => setContractSearch(e.target.value)}
                  />
                  {contractResults.length > 0 && (
                    <div className={styles.resultList}>
                      {contractResults.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className={styles.resultButton}
                          onClick={() => {
                            setFilterContractId(c.id);
                            setContractSearch("");
                            setContractResults([]);
                          }}
                        >
                          <span>{c.number}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {filterContractId != null && (
                  <div className={styles.filterChip}>
                    <span>
                      Vertrag:{" "}
                      {contractOptions.find((c) => c.id === filterContractId)
                        ?.number ?? `Vertrag ${filterContractId}`}
                    </span>
                    <button
                      type="button"
                      className={styles.filterChipBtn}
                      onClick={() => setFilterContractId(null)}
                    >
                      entfernen
                    </button>
                  </div>
                )}
              </div>

              {/* Risikotyp */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Risikotyp</label>
                <select
                  className={styles.select}
                  value={selectedRiskBranch}
                  onChange={(e) => {
                    setSelectedRiskBranch(e.target.value);
                    setSelectedRiskId(null);
                  }}
                >
                  <option value="">Alle Typen</option>
                  {branchOptions.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>

              {/* Risiko-Suche */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Versichertes Risiko</label>
                <span className={styles.fieldHint}>
                  Risikoobjekt suchen und übernehmen (optional).
                </span>
                <div className={styles.searchWrap}>
                  <input
                    className={styles.input}
                    type="text"
                    placeholder="Risikoobjekt suchen"
                    value={riskSearch}
                    onChange={(e) => {
                      setRiskSearch(e.target.value);
                      setSelectedRiskId(null);
                    }}
                  />
                  {filteredRisks.length > 0 && riskSearch && (
                    <div className={styles.resultList}>
                      {filteredRisks.slice(0, 50).map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          className={mergeClasses(styles.resultButton, (selectedRiskId === r.id) && styles.resultButtonSelected)}
                          onClick={() => {
                            setSelectedRiskId(r.id);
                            setRiskSearch(r.riskName);
                          }}
                        >
                          <span>{r.riskName}</span>
                          <span className={styles.resultMeta}>
                            {r.branch || "—"} · Vertrag{" "}
                            {contractOptions.find(
                              (c) => c.id === r.contractID,
                            )?.number ?? r.contractID}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Allgemein cards — only when risk selected */}
          {selectedRisk && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Zuordnung</h3>
              <div className={styles.assignmentGrid}>
                <div className={styles.assignmentCard}>
                  <span className={styles.assignmentIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
                    </svg>
                  </span>
                  <div className={styles.assignmentText}>
                    <span className={styles.assignmentLabel}>Kontakt</span>
                    <span className={styles.assignmentValue}>
                      {selectedContactName || "—"}
                    </span>
                    <span className={styles.assignmentMeta}>
                      Kontakt aus Risikoübernahme
                    </span>
                  </div>
                </div>
                <div className={styles.assignmentCard}>
                  <span className={styles.assignmentIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="6" y="3" width="13" height="18" rx="2" />
                      <line x1="9" y1="8" x2="16" y2="8" />
                      <line x1="9" y1="12" x2="16" y2="12" />
                      <line x1="9" y1="16" x2="13" y2="16" />
                    </svg>
                  </span>
                  <div className={styles.assignmentText}>
                    <span className={styles.assignmentLabel}>Vertrag</span>
                    <span className={styles.assignmentValue}>
                      {selectedContractNbr || "—"}
                    </span>
                    <span className={styles.assignmentMeta}>
                      Vertrag aus Risikoübernahme
                    </span>
                  </div>
                </div>
                <div className={styles.assignmentCard}>
                  <span className={styles.assignmentIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3l8 3v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z" />
                    </svg>
                  </span>
                  <div className={styles.assignmentText}>
                    <span className={styles.assignmentLabel}>Risiko</span>
                    <span className={styles.assignmentValue}>
                      {selectedRisk.riskName}
                    </span>
                    <span className={styles.assignmentMeta}>
                      {selectedRisk.branch || "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Schaden */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Schaden</h3>
            <div className={styles.fieldsThree}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Schadenart</label>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="z. B. Wasser, Sturm, Glas …"
                  value={schaden}
                  onChange={(e) => setSchaden(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Status</label>
                <select
                  className={styles.select}
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">Status wählen</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Meldedatum</label>
                <input
                  className={styles.input}
                  type="date"
                  value={erstellt}
                  onChange={(e) => setErstellt(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Schadentag</label>
                <input
                  className={styles.input}
                  type="date"
                  value={schadentag}
                  onChange={(e) => setSchadentag(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Schadenort</label>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="Adresse / Ort des Schadens"
                  value={schadenort}
                  onChange={(e) => setSchadenort(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Meldung</label>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="z. B. Telefon, E-Mail, Post"
                  value={meldung}
                  onChange={(e) => setMeldung(e.target.value)}
                />
              </div>
              <div className={mergeClasses(styles.field, styles.fullRow)}>
                <label className={styles.fieldLabel}>Versicherte Gefahr</label>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="z. B. Leitungswasser, Sturm/Hagel …"
                  value={versicherteGefahr}
                  onChange={(e) => setVersicherteGefahr(e.target.value)}
                />
              </div>
              <div className={mergeClasses(styles.field, styles.fullRow)}>
                <label className={styles.fieldLabel}>Schadenbeschreibung</label>
                <textarea
                  className={styles.textarea}
                  placeholder="Beschreibung des Schadenhergangs"
                  value={beschreibung}
                  onChange={(e) => setBeschreibung(e.target.value)}
                />
              </div>
              {isKfzBranch && (
                <>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Fahrer</label>
                    <input
                      className={styles.input}
                      type="text"
                      placeholder="Name des Fahrers"
                      value={fahrer}
                      onChange={(e) => setFahrer(e.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Schadenursache</label>
                    <input
                      className={styles.input}
                      type="text"
                      placeholder="z. B. Auffahrunfall"
                      value={schadenursache}
                      onChange={(e) => setSchadenursache(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Finanzen */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Finanzen</h3>
            <div className={styles.fieldsThree}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Schadenhöhe (€)</label>
                <input
                  className={styles.input}
                  type="number"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={schadenhoehe}
                  onChange={(e) => setSchadenhoehe(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Selbstbeteiligung (€)</label>
                <input
                  className={styles.input}
                  type="number"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={sb}
                  onChange={(e) => setSb(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Zahlung (€)</label>
                <input
                  className={styles.input}
                  type="number"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={zahlung}
                  onChange={(e) => setZahlung(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Bearbeitung */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Bearbeitung</h3>
            <div className={styles.fields}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Anspruchsteller</label>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="Name"
                  value={anspruchsteller}
                  onChange={(e) => setAnspruchsteller(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Vorgangs-Nr.</label>
                <select
                  className={styles.select}
                  value={vorgangsNr}
                  onChange={(e) => setVorgangsNr(e.target.value)}
                >
                  <option value="">Keine Auswahl</option>
                  {VORGANG_OPTIONS.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Sachbearbeiter</label>
                <select
                  className={styles.select}
                  value={sachbearbeiter}
                  onChange={(e) => setSachbearbeiter(e.target.value)}
                >
                  <option value="">Keine Auswahl</option>
                  {SACHBEARBEITER_OPTIONS.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  Kontakt (manuell, falls kein Risiko gewählt)
                </label>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="Name des Kontakts"
                  value={kontakt}
                  onChange={(e) => setKontakt(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  Vertrag (manuell, falls kein Risiko gewählt)
                </label>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="Vertragsnummer"
                  value={vertrag}
                  onChange={(e) => setVertrag(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => navigate(-1)}
          >
            Abbrechen
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={handleSave}
          >
            Speichern
          </button>
        </div>
      </div>

      {toast && (
        <div
          className={mergeClasses(styles.toast, toast.kind === "success" ? styles.toastSuccess : styles.toastError)}
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default SchadenForm;
